/**
 * Where a tenant transfers rent, shown when nobody is going to debit them
 * (COL-533, Neel 13 Oct).
 *
 * Rendered "where the mockup shows SEPA" in exactly two states: the tenant
 * selected bank transfer, or has no payment method at all. In both, without
 * these details they have no way to pay.
 *
 * THE DESTINATION IS RESOLVED PER ROOM, NOT FROM A BUILD VARIABLE. This used
 * to read `VITE_COLIVIQ_IBAN`, a third source of truth next to V1's per-room
 * resolution and the `platform_bank_accounts` table. That variable is unset
 * in dev, so the card showed "Bank details on request" where V1 had always
 * shown a real IBAN -- tenants lost it in the V2 switch (Neel, 14 Oct: "why
 * cant we show the IBAN for rent to tenants anymore?", and on the fix: "that
 * makes total sense and should never have been changed otherwise").
 *
 * The caller now resolves it through `resolveBankTransferDetails`, the same
 * room_payment_config -> get_room_rent_account -> CoLiviq-last-resort chain
 * V1 uses, so the two dashboards cannot name different accounts. This
 * component only renders what it is handed.
 *
 * `details = null` is a real answer, not a loading state: an owner_collects
 * room whose owner bank account is not configured has no legitimate
 * destination, and it renders the "on request" line rather than inventing
 * one. A wrong IBAN on a rent screen sends real money to the wrong account.
 */
import type { BankTransferDetails } from "@/lib/rent-bank-transfer";

type Labels = {
  bankTransferHeading?: string;
  bankAccountHolder?: string;
  bankIban?: string;
  bankBic?: string;
  bankDetailsUnavailable?: string;
};

export default function TenantBankTransferDetails({
  labels,
  details,
}: {
  labels: Labels;
  /**
   * The resolved destination for THIS room, or null when there is none.
   *
   * Resolved by the caller via `resolveBankTransferDetails` so V1 and V2
   * cannot name different accounts. Null renders the "on request" line.
   */
  details: BankTransferDetails | null;
}) {
  return (
    <div className="rounded-2xl bg-muted/40 p-4">
      <p className="font-semibold text-foreground">
        {labels.bankTransferHeading}
      </p>

      {details ? (
        <>
          <dl className="mt-3 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-sm text-muted-foreground">
                {labels.bankAccountHolder}
              </dt>
              {/*
                The RESOLVED holder, not the SEPA creditor name. Under
                owner_collects the money goes to the owner's account, and
                labelling it "CoLiviq" would name the wrong recipient on a
                screen telling a tenant where to send rent.
              */}
              <dd className="font-medium text-foreground">
                {details.accountHolder}
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-sm text-muted-foreground">
                {labels.bankIban}
              </dt>
              {/*
                `break-all` and a tabular font: an IBAN is 22 characters that
                must stay readable, and copyable, on a narrow phone rather
                than widening the card into horizontal scroll.
              */}
              <dd className="break-all font-mono font-medium text-foreground">
                {details.iban}
              </dd>
            </div>
            {details.bic ? (
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="text-sm text-muted-foreground">
                  {labels.bankBic}
                </dt>
                <dd className="break-all font-mono font-medium text-foreground">
                  {details.bic}
                </dd>
              </div>
            ) : null}
          </dl>
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          {labels.bankDetailsUnavailable}
        </p>
      )}
    </div>
  );
}
